<template>
  <v-layout row wrap>
    <v-flex xs10 offset-xs1 lg8 offset-lg2>
      <v-form v-model="valid" ref="form" @submit.prevent="submit">
        <v-card class="pa-3" v-if="!loading">
          <v-card-text>
            <v-alert
              color="error"
              icon="error"
              value="true"
              class="mb-3"
              v-if="!form.isActive"
            >
              {{ $t("message.info.contractInactive") }}
            </v-alert>
            <v-alert
              color="warning"
              icon="warning"
              value="true"
              class="mb-3"
              v-else-if="form.isInUse"
            >
              {{ $t("message.info.contractInUse") }}
            </v-alert>

            <div class="subheading mb-3">{{ $t("label.basicInfo") }}</div>
            <v-layout row>
              <v-flex xs3 lg2>
                <label class="form-label"
                  >{{ $t("label.contractNumber") }}*</label
                >
              </v-flex>
              <v-flex xs2 lg2>
                <v-text-field
                  :disabled="disabled"
                  v-model="form.number"
                  placeholder="NNNNNN/AA"
                  :mask="validation.mask.contract"
                  return-masked-value
                  :rules="validation.required"
                  :maxlength="constants.LENGTH_CONTRACT"
                ></v-text-field>
              </v-flex>
            </v-layout>
            <v-layout row>
              <v-flex xs3 lg2>
                <label class="form-label">{{ $t("label.description") }}*</label>
              </v-flex>
              <v-flex xs5 lg4>
                <v-text-field
                  :disabled="disabled"
                  v-model="form.description"
                  :rules="validation.text"
                  :maxlength="constants.LENGTH_CONTRACT_DESCRIPTION"
                ></v-text-field>
              </v-flex>
            </v-layout>
            <v-layout row class="mb-4">
              <v-flex xs3 lg2>
                <label class="form-label">{{
                  $t("label.contractObject")
                }}</label>
              </v-flex>
              <v-flex xs9 lg10>
                <v-text-field
                  multi-line
                  :disabled="disabled"
                  v-model="form.subjectMatter"
                  auto-grow
                  :counter="constants.LENGTH_CONTRACT_OBJECT"
                  :maxlength="constants.LENGTH_CONTRACT_OBJECT"
                ></v-text-field>
              </v-flex>
            </v-layout>
            <v-layout row>
              <v-flex xs3 lg2>
                <label class="form-label">{{ $t("label.office") }}*</label>
              </v-flex>
              <v-flex xs7 lg10>
                <v-select
                  autocomplete
                  :disabled="disabled"
                  v-bind:items="offices"
                  v-model="form.office.id"
                  :rules="validation.required"
                  :no-data-text="$t('message.warning.noData')"
                ></v-select>
              </v-flex>
            </v-layout>
            <v-layout row>
              <v-flex xs3 lg2>
                <label class="form-label">{{ $t("label.warranty") }}</label>
              </v-flex>
              <v-flex xs2 lg2>
                <v-text-field
                  :disabled="disabled"
                  v-model.number="form.warrantyDays"
                  :mask="validation.mask.warrantyDays"
                  :suffix="$t('label.days')"
                  @change="clearWarrantyDate"
                ></v-text-field>
              </v-flex>
              <v-flex xs3 lg2 class="ml-3">
                <label class="form-label">{{ $t("label.warrantyValidity") }}</label>
              </v-flex>
              <v-flex xs2 lg2>
                <app-menu-date-picker
                  :disabled="disabled"
                  :model.sync="form.warrantyValidityTerm.start"
                  :rules="warrantValidityTermStartRule"
                  @change="clearWarrantyDays"
                ></app-menu-date-picker>
              </v-flex>
              <v-flex xs2 lg2 class="ml-3">
                <app-menu-date-picker
                  :disabled="disabled"
                  :model.sync="form.warrantyValidityTerm.finish"
                  :rules="warrantvalidityTermFinishRule"
                  @change="clearWarrantyDays"
                ></app-menu-date-picker>
              </v-flex>
              <v-flex xs1 class="pt-2 pl-2">
                <v-icon @click="clearWarrantyDaysAndDate" style="cursor: pointer">cancel</v-icon>
              </v-flex>
            </v-layout>
            <v-layout row>
              <v-flex xs3 lg2>
                <label class="form-label">{{ $t("label.validity") }}*</label>
              </v-flex>
              <v-flex xs2 lg2>
                <app-menu-date-picker
                  :disabled="disabled"
                  :model.sync="form.validityTerm.start"
                  :rules="validation.required"
                ></app-menu-date-picker>
              </v-flex>
              <v-flex xs2 lg2 class="ml-3">
                <app-menu-date-picker
                  :disabled="disabled"
                  :model.sync="form.validityTerm.finish"
                  :rules="validityTermFinishRule"
                ></app-menu-date-picker>
              </v-flex>
            </v-layout>
            <v-layout row>
              <v-flex xs3 lg2>
                <label class="form-label">{{ $t("label.unit") }}*</label>
              </v-flex>
              <v-flex xs4 lg3>
                <v-select
                  autocomplete
                  :disabled="disabled"
                  v-bind:items="units"
                  v-model="form.unit.id"
                  :rules="validation.required"
                  :maxlength="constants.LENGTH_UNIT"
                  :no-data-text="$t('message.warning.noData')"
                ></v-select>
              </v-flex>
            </v-layout>
            <v-layout row>
              <v-flex xs3 lg2>
                <label class="form-label">{{ $t("label.unitCost") }}*</label>
              </v-flex>
              <v-flex xs2 lg2>
                <v-text-field
                  :disabled="disabled"
                  v-model="form.unitCost"
                  :rules="validation.requiredBiggerThanZero"
                  :maxlength="constants.LENGTH_UNIT_COST"
                  prefix="R$"
                ></v-text-field>
              </v-flex>
            </v-layout>
            <v-layout row>
              <v-flex xs3 lg2>
                <label class="form-label">{{ $t("label.hiredAmount") }}</label>
              </v-flex>
              <v-flex xs2 lg2>
                <v-text-field
                  :disabled="disabled"
                  v-model.number="form.hiredAmount"
                  :mask="validation.mask.hiredAmount"
                  :suffix="unit"
                ></v-text-field>
              </v-flex>
              <v-flex xs3 lg2>
                <label class="form-label">{{ $t("label.estimatedQuantity") }}</label>
              </v-flex>
              <v-flex xs2 lg2>
                <v-text-field
                  :disabled=true
                  v-model.number="form.estimatedTime"
                  :suffix="unit"
                ></v-text-field>
              </v-flex>
              <v-flex xs3 lg2>
                <label class="form-label">{{ $t("label.executedQuantity") }}</label>
              </v-flex>
              <v-flex xs2 lg2>
                <v-text-field
                  :disabled=true
                  v-model.number="form.executedTime"
                  :suffix="unit"
                ></v-text-field>
              </v-flex>
            </v-layout>

            <div class="subheading mt-3">{{ $t("label.contractItem") }}</div>
            <p v-html="$t('message.info.contractItems', { unit })"></p>

            <v-layout>
              <v-flex xs1 lg1>
                <label class="form-label center">Item</label>
              </v-flex>

              <v-flex xs3 lg3 class="mr-2">
                <label class="form-label center">Nome da cesta</label>
              </v-flex>

              <v-flex xs2 lg2 class="mr-2">
                <label class="form-label center">{{ unit.charAt(0).toUpperCase() + unit.slice(1) }} contratadas</label>
              </v-flex>
              <v-flex xs2 lg2 class="mr-2">
                <label class="form-label center">{{ unit.charAt(0).toUpperCase() + unit.slice(1) }} orçadas</label>
              </v-flex>
              <v-flex xs2 lg2 class="mr-2">
                <label class="form-label center">{{ unit.charAt(0).toUpperCase() + unit.slice(1) }} executadas</label>
              </v-flex>
            </v-layout>

            <app-contract-item
              v-for="(item, index) in form.items"
              :key="item.uuid"
              :model="item"
              :index="index"
              :suffix="unit"
              :isInUse="form.isInUse"
              :disabled="disabled"
              v-on:remove="removeItem"
              v-on:change="updateItem"
            ></app-contract-item>
            <v-layout row v-if="form.isInUse && !contractCopy">
              <span>**Cesta em uso</span>
            </v-layout>
          </v-card-text>

          <v-card-actions v-if="!disabled">
            <v-btn
              :disabled="removing"
              :loading="saving"
              @click="save"
              color="primary"
            >
              {{ $t("label.save") }}
            </v-btn>
            <v-btn :disabled="saving || removing" @click="addItem">
              <v-icon class="mr-3">account_balance_wallet</v-icon>
              {{ $t("label.addContractItem") }}
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              v-if="contractExists && this.form.isInUse === true && this.form.office.controlContract === true"
              @click="copyContractButton"
            >
              <v-icon class="mr-3">content_copy</v-icon>
              {{ $t("label.contractCopy") }}
            </v-btn>
            <v-btn
              v-if="contractExists && this.form.isInUse === true && this.form.office.controlContract === true"
              @click="updateContractButton"
            >
              <v-icon class="mr-3">cached</v-icon>
              {{ $t("label.contractUpdate") }}
            </v-btn>
            <v-btn
              :disabled="saving"
              :loading="removing"
              @click="remove"
              color="error"
              v-if="!!form.id && !contractCopy"
            >
              {{ form.isInUse ? $t("label.inactivate") : $t("label.remove") }}
            </v-btn>
          </v-card-actions>
        </v-card>
        <v-card v-else>
          <app-card-loader></app-card-loader>
        </v-card>
      </v-form>
    </v-flex>
  </v-layout>
</template>

<script>
import moment from "moment";
import constants from "@/helpers/constants";
import utilities from "@/helpers/utilities";
import validation from "@/helpers/validation";
import CardLoader from "@/components/shared/CardLoader";
import MenuDatePicker from "@/components/shared/MenuDatePicker";
import Contract from "@/helpers/entities/Contract";
import ContractItem from "./ContractItem";

export default {
  data() {
    return {
      constants,
      validation,
      valid: true,

      saving: false,
      loading: true,
      removing: false,

      form: Contract.build(),
      units: this.$store.getters.unitsValueText,
      offices: this.$store.getters.officesValueText,

      warrantValidityTermStartRule: [
        () => {
          if (
            this.form.warrantyValidityTerm.start == null &&
            this.form.warrantyValidityTerm.finish != null
          ) {
            return this.$t("message.error.emptyStartDate");
          } else if (moment(this.form.warrantyValidityTerm.start)
                        .isBefore(this.form.validityTerm.start)) {
            return this.$t("message.error.validityStart");
          }
          return true;
        }
      ],
      warrantvalidityTermFinishRule: [
        () => {
          if (
            this.form.warrantyValidityTerm.start != null &&
            this.form.warrantyValidityTerm.finish == null
          ) {
            return this.$t("message.error.emptyFinishDate");
          } else if (
                moment(this.form.warrantyValidityTerm.finish)
                        .isBefore(this.form.warrantyValidityTerm.start)
          ) {
            return this.$t("message.error.validityFinish");
          }
          return true;
        },
      ],
      validityTermFinishRule: [
        ...validation.required,
        () =>
          this.form.validityTerm.finish >= this.form.validityTerm.start ||
          this.$t("message.error.period"),
      ],
    };
  },

  computed: {
    unit() {
      if (!this.form.unit.id) return "";
      return this.$store.getters.unitsDictionary[
        this.form.unit.id
      ].name.toLowerCase();
    },
    disabled() {
      return !this.$store.getters.isAdmin || !this.form.isActive;
    },

    contractExists() {
      if (this.$route.name === "contractEdit") {
        return true;
      }
      return false;
    },

    contractCopy() {
      if (this.$route.name === "contractCopy") {
        this.form.id = 0;
        this.form.estimatedTime = 0;
        this.form.executedTime = 0;
        for (let i = 0; i < this.form.items.length; i++) {
          this.form.items[i].estimatedTime = 0;
          this.form.items[i].executedTime = 0;
        }
        return true;
      }
      return false;
    },
  },

  components: {
    appCardLoader: CardLoader,
    appContractItem: ContractItem,
    appMenuDatePicker: MenuDatePicker,
  },

  watch: {
    $route: "fetch",
  },

  created() {
    this.fetch();
  },

  methods: {
    addItem() {
      this.form.items.splice(this.form.items.length, 0, Contract.item.build());
    },
    removeItem(uuid) {
      const index = this.form.items.map((item) => item.uuid).indexOf(uuid);
      this.form.items.splice(index, 1);
    },

    updateItem(value) {
      const index = this.form.items
        .map((item) => item.uuid)
        .indexOf(value.uuid);
      this.form.items.splice(index, 1, value);
    },

    clearWarrantyDays() {
      this.form.warrantyDays = null;
    },

    clearWarrantyDate() {
      this.form.warrantyValidityTerm.start = null;
      this.form.warrantyValidityTerm.finish = null;
    },

    clearWarrantyDaysAndDate() {
      this.clearWarrantyDays();
      this.clearWarrantyDate();
    },

    async fetch() {
      this.loading = true;

      const id = this.$route.params.id;

      if (!utilities.isEmptyOrZero(id)) {
        await this.$store.dispatch("fetchContract", id);
        this.form = Contract.toForm(this.$store.getters.contract);
      }

      this.loading = false;
    },

    async remove() {
      this.removing = true;
      await this.$store.dispatch(
        "removeContract",
        Contract.toPayload(this.form)
      );
      this.$router.push({ name: "contractIndex" });
    },

    async save() {
      if (!this.$refs.form.validate()) return;

      this.saving = true;
      await this.$store.dispatch(
        "saveOrUpdateContract",
        Contract.toPayload(this.form)
      );
      this.$router.push({ name: "contractIndex" });
    },

    async copyContractButton() {
      this.$router.push({ name: "contractCopy" });
    },

    async updateContractButton() {
      this.$router.push({ name: "contractUpdate" });
    },
  },
};
</script>

<style>
  .center {
    text-align: center !important;
    margin-right: 0 !important;
    margin-bottom: 4px;
  }
</style>


// WEBPACK FOOTER //
// src/components/contracts/Edit.vue